// XLTable - Sprint 44 XL.
// Tabla moderna con sticky header, loading state (skeleton), hover y
// navegacion por teclado en filas clickeables. Soporte dark mode via tokens.
//
// Uso:
//   <XLTable
//     columns={[{key:'numero', label:'#'}, {key:'total', label:'Total', align:'right'}]}
//     data={rows}
//     loading={false}
//     onRowClick={(row) => ...}
//     rowKey="id"
//   />
//
// Columna soporta opcional render(row) -> ReactNode para celdas custom.
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import XLSkeleton from './XLSkeleton';

const CSS_ID = 'xl-table-css';
const CSS = `
.xl-table-wrap {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  overflow: hidden;
  box-shadow: var(--xl-shadow-sm);
  font-family: var(--xl-font-sans);
}
.xl-table-scroll { max-height: 72vh; overflow: auto }
.xl-table { width: 100%; border-collapse: collapse; min-width: 640px }

.xl-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--xl-color-bg);
  color: var(--xl-color-text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  text-align: left;
  white-space: nowrap;
}
.xl-table tbody td {
  padding: 14px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  color: var(--xl-color-text);
  font-size: 14px;
  vertical-align: middle;
}
.xl-table tbody tr {
  transition: background 120ms cubic-bezier(0,0,0.2,1);
}
.xl-table tbody tr.xl-clickable { cursor: pointer }
.xl-table tbody tr.xl-clickable:hover { background: var(--xl-color-bg) }
.xl-table tbody tr.xl-clickable:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: -2px;
}
.xl-table .xl-empty {
  text-align: center;
  color: var(--xl-color-text-muted);
  padding: 56px 16px;
  font-size: 14px;
}
.xl-table .xl-td-right  { text-align: right }
.xl-table .xl-td-center { text-align: center }
`;

function alignClass(a) {
  if (a === 'right')  return 'xl-td-right';
  if (a === 'center') return 'xl-td-center';
  return '';
}

export default function XLTable({
  columns = [],
  data = [],
  loading = false,
  onRowClick,
  emptyMessage = 'Sin datos',
  rowKey = 'id',
  ariaLabel,
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const skeletonRows = 5;

  return (
    <div className="xl-table-wrap">
      <div className="xl-table-scroll">
        <table className="xl-table" role="table" aria-label={ariaLabel}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={alignClass(c.align)}
                  style={c.width ? { width: c.width } : undefined}
                  scope="col"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: skeletonRows }).map((_, i) => (
              <tr key={'sk-' + i}>
                {columns.map((c) => (
                  <td key={c.key} className={alignClass(c.align)}>
                    <XLSkeleton variant="line" width="80%" />
                  </td>
                ))}
              </tr>
            ))}

            {!loading && data.length === 0 && (
              <tr>
                <td className="xl-empty" colSpan={columns.length}>
                  {emptyMessage}
                </td>
              </tr>
            )}

            {!loading && data.length > 0 && data.map((row, idx) => {
              const key = row[rowKey] != null ? row[rowKey] : idx;
              const clickable = typeof onRowClick === 'function';
              const onKey = clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }
                : undefined;
              return (
                <tr
                  key={key}
                  className={
                    'xl-motion-fade-in '
                    + (clickable ? 'xl-clickable' : '')
                  }
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  onKeyDown={onKey}
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? 'button' : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={alignClass(c.align)}>
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
